import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RTooltip,
    ResponsiveContainer
} from 'recharts';
import { ArrowLeft, Bell, Pencil, MapPin, Phone, Mail, IdCard, Landmark, User as UserIcon, FileText, Download, Trash2, ChevronDown, ChevronRight, Zap } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { VendorRenameMergeDialog } from '../components/VendorRenameMergeDialog';
import { VendorPaymentsPanel } from '../components/VendorPaymentsPanel';
import { VendorReconciliationPanel } from '../components/VendorReconciliationPanel';
import { OpeningBalanceEditor } from '../components/OpeningBalanceEditor';
import { api, fmtDate, fmtMoney, fmtMonth } from '../lib/api';

function Kpi({ label, value, testid }) {
    return (
        <div className="p-4 rounded-lg border border-border bg-card/70">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="font-heading text-2xl mt-1 tabular-nums" data-testid={testid}>
                {value}
            </div>
        </div>
    );
}

function DetailRow({ icon: Icon, label, value, mono = false, testid }) {
    if (!value) return null;
    return (
        <div className="flex items-start gap-3 text-sm">
            <div className="h-8 w-8 rounded-md bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                <Icon size={14} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
                <div
                    className={`mt-0.5 break-words ${mono ? 'font-mono tabular-nums' : ''}`}
                    data-testid={testid}
                >
                    {value}
                </div>
            </div>
        </div>
    );
}

function fmtBytes(n) {
    if (n == null) return '';
    const b = Number(n);
    if (!Number.isFinite(b) || b < 0) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function UploadedLedgersSection({ vendorKey, vendorLabel }) {
    // 2026-07: shows every ledger PDF the user has ever uploaded for this
    // vendor, latest first. Users can view / re-download / delete each one.
    // Collapsed by default; expanding hits the API only once per mount.
    const [open, setOpen] = useState(false);
    const [ledgers, setLedgers] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.listVendorLedgerFiles(vendorKey);
            setLedgers(r?.ledgers || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load ledger history');
            setLedgers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && ledgers == null && !loading) {
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleDownload = async (led) => {
        setBusyId(led.id);
        try {
            const resp = await api.downloadVendorLedgerFile(vendorKey, led.id);
            const blob = resp.data;
            const filename = led.source_filename || `ledger-${led.id}.pdf`;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Download failed');
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (led) => {
        setBusyId(led.id);
        try {
            await api.deleteVendorLedgerFile(vendorKey, led.id);
            toast.success('Ledger removed');
            setLedgers((cur) => (cur || []).filter((x) => x.id !== led.id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Delete failed');
        } finally {
            setBusyId(null);
            setConfirmDelete(null);
        }
    };

    const count = ledgers?.length ?? null;

    return (
        <Card className="p-5 border-border/80" data-testid="vendor-uploaded-ledgers-card">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-3 text-left"
                data-testid="uploaded-ledgers-toggle"
                aria-expanded={open}
            >
                {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <div className="flex-1 min-w-0">
                    <div className="font-heading text-base">Uploaded ledgers</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {count == null
                            ? `Every ledger you have uploaded for ${vendorLabel || 'this vendor'} is saved here.`
                            : count === 0
                                ? 'No ledgers saved yet. Upload one via the reconciliation panel above.'
                                : `${count} ledger${count === 1 ? '' : 's'} saved · click to ${open ? 'hide' : 'view'}`}
                    </div>
                </div>
                {count != null && count > 0 && (
                    <Badge variant="secondary" data-testid="uploaded-ledgers-count">{count}</Badge>
                )}
            </button>

            {open && (
                <div className="mt-4 pt-4 border-t border-border/80" data-testid="uploaded-ledgers-body">
                    {loading && (
                        <div className="space-y-2">
                            <Skeleton className="h-12" />
                            <Skeleton className="h-12" />
                        </div>
                    )}
                    {!loading && (ledgers || []).length === 0 && (
                        <div className="text-sm text-muted-foreground py-4 text-center">
                            No ledgers uploaded yet.
                        </div>
                    )}
                    {!loading && (ledgers || []).length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border/70">
                                        <th className="py-2 pr-3 font-medium">Uploaded</th>
                                        <th className="py-2 pr-3 font-medium">Period</th>
                                        <th className="py-2 pr-3 font-medium">Closing</th>
                                        <th className="py-2 pr-3 font-medium">Filename</th>
                                        <th className="py-2 pr-3 font-medium text-right">Size</th>
                                        <th className="py-2 pr-1 font-medium text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(ledgers || []).map((led) => {
                                        const period = [led.period_from, led.period_to].filter(Boolean).join(' \u2192 ');
                                        const closing = led.closing_balance != null
                                            ? `${fmtMoney(led.closing_balance)}${led.closing_direction ? ' ' + led.closing_direction : ''}`
                                            : '—';
                                        return (
                                            <tr key={led.id} className="border-b border-border/40 last:border-b-0" data-testid={`ledger-row-${led.id}`}>
                                                <td className="py-3 pr-3">
                                                    <div className="flex items-center gap-2">
                                                        <FileText size={14} className="text-muted-foreground shrink-0" />
                                                        <span className="tabular-nums">{fmtDate(led.uploaded_at)}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 pr-3 text-muted-foreground tabular-nums">
                                                    {period || '—'}
                                                </td>
                                                <td className="py-3 pr-3 tabular-nums font-mono">{closing}</td>
                                                <td className="py-3 pr-3 max-w-[200px] truncate" title={led.source_filename}>
                                                    {led.source_filename || 'ledger.pdf'}
                                                </td>
                                                <td className="py-3 pr-3 text-right tabular-nums text-muted-foreground text-xs">
                                                    {fmtBytes(led.size_bytes)}
                                                </td>
                                                <td className="py-3 pr-1 text-right whitespace-nowrap">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => handleDownload(led)}
                                                        disabled={busyId === led.id}
                                                        data-testid={`ledger-download-${led.id}`}
                                                        title="Download original"
                                                    >
                                                        <Download size={14} />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => setConfirmDelete(led)}
                                                        disabled={busyId === led.id}
                                                        data-testid={`ledger-delete-${led.id}`}
                                                        title="Delete ledger"
                                                        className="text-destructive hover:text-destructive"
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent data-testid="confirm-delete-ledger-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this ledger?</AlertDialogTitle>
                        <AlertDialogDescription>
                            The original PDF/image will be permanently removed from storage. The reconciliation
                            summary for this vendor is not affected.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-delete-ledger-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => confirmDelete && handleDelete(confirmDelete)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="confirm-delete-ledger-confirm"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}

function VendorDetailsCard({ data }) {
    // 2026-01: display vendor/buyer metadata extracted from bills. All fields
    // are optional — if the OCR never saw them and the user hasn't manually
    // filled them in, the row is skipped. We hide the entire card only when
    // NOTHING is available.
    const rows = [
        { key: 'gstin', label: 'Vendor GSTIN', value: data.vendor_gstin, icon: Landmark, mono: true, testid: 'vendor-detail-gstin' },
        { key: 'pan', label: 'Vendor PAN', value: data.vendor_pan, icon: IdCard, mono: true, testid: 'vendor-detail-pan' },
        { key: 'address', label: 'Vendor address', value: data.vendor_address, icon: MapPin, mono: false, testid: 'vendor-detail-address' },
        { key: 'phone', label: 'Phone', value: data.phone, icon: Phone, mono: true, testid: 'vendor-detail-phone' },
        { key: 'email', label: 'Email', value: data.vendor_email, icon: Mail, mono: false, testid: 'vendor-detail-email' },
        { key: 'buyer_gstin', label: 'Buyer GSTIN (on their bill)', value: data.buyer_gstin, icon: Landmark, mono: true, testid: 'vendor-detail-buyer-gstin' },
        { key: 'buyer_pan', label: 'Buyer PAN (on their bill)', value: data.buyer_pan, icon: IdCard, mono: true, testid: 'vendor-detail-buyer-pan' },
        { key: 'buyer_name', label: 'Buyer name (on their bill)', value: data.buyer_name, icon: UserIcon, mono: false, testid: 'vendor-detail-buyer-name' },
    ];
    const hasAny = rows.some((r) => r.value);

    return (
        <Card className="p-5 border-border/80" data-testid="vendor-details-card">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="font-heading text-lg">Vendor details</div>
                    <div className="text-xs text-muted-foreground">
                        Extracted from the latest bill. Used for CA exports and vendor communication.
                    </div>
                </div>
            </div>
            {hasAny ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {rows.map((r) => (
                        <DetailRow
                            key={r.key}
                            icon={r.icon}
                            label={r.label}
                            value={r.value}
                            mono={r.mono}
                            testid={r.testid}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-sm text-muted-foreground py-2" data-testid="vendor-details-empty">
                    No vendor details captured yet. They will appear here once you upload a bill from this vendor that prints address, GSTIN, PAN or phone number.
                </div>
            )}
        </Card>
    );
}

export default function VendorDetailPage() {
    const { vendorKey } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [renameOpen, setRenameOpen] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const r = await api.getVendor(vendorKey);
            setData(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Vendor not found');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load(); // eslint-disable-next-line
    }, [vendorKey]);

    const chartData = useMemo(() => {
        if (!data) return [];
        // ascending by month for chart
        return [...data.months].reverse().map((m) => ({ month: m.month, total: m.total, count: m.count }));
    }, [data]);

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-24" />
                <Skeleton className="h-64" />
            </div>
        );
    }
    if (!data) return <div>Vendor not found.</div>;

    return (
        <div className="space-y-5 animate-fade-in" data-testid="vendor-detail-page">
            <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => nav('/vendors')} className="gap-2" data-testid="vendor-detail-back">
                    <ArrowLeft size={16} /> All vendors
                </Button>
                <div className="flex items-center gap-2">
                    {data.active_alerts_count > 0 && (
                        <Link to={`/alerts`}>
                            <Badge className="gap-1 bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border border-[hsl(var(--warning-border))]">
                                <Bell size={12} /> {data.active_alerts_count} active alert{data.active_alerts_count > 1 ? 's' : ''}
                            </Badge>
                        </Link>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => setRenameOpen(true)}
                        data-testid="vendor-detail-rename-btn"
                    >
                        <Pencil size={14} /> Rename / Merge
                    </Button>
                    {/* Sprint 2026-08: bulk re-run OCR through Claude Sonnet 5
                        for every bill of this vendor. One-tap cleanup for
                        historical data captured with earlier OCR versions. */}
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={async () => {
                            const n = data.bills_count || 0;
                            if (n === 0) {
                                toast.info('No bills to rescan for this vendor.');
                                return;
                            }
                            // eslint-disable-next-line no-alert
                            const ok = window.confirm(
                                `Rescan all ${n} bill${n > 1 ? 's' : ''} of "${data.vendor}" using AI?\n\n`
                                + 'Existing line items will be replaced by fresh extractions. This may take a few minutes.',
                            );
                            if (!ok) return;
                            const tid = toast.loading(`Rescanning ${n} bills…`);
                            try {
                                const res = await api.bulkRescanBills({
                                    vendor_query: data.vendor,
                                    limit: Math.min(200, Math.max(n, 50)),
                                });
                                toast.dismiss(tid);
                                if (res.succeeded === res.attempted) {
                                    toast.success(
                                        `Rescanned ${res.succeeded} bill${res.succeeded > 1 ? 's' : ''} successfully`,
                                    );
                                } else {
                                    toast.warning(
                                        `${res.succeeded} succeeded, ${res.failed} failed. Check individual bills for details.`,
                                    );
                                }
                                await load();
                            } catch (e) {
                                toast.dismiss(tid);
                                toast.error(e?.response?.data?.detail || 'Bulk rescan failed');
                            }
                        }}
                        data-testid="vendor-bulk-rescan-btn"
                    >
                        <Zap size={14} /> Rescan all with AI
                    </Button>
                </div>
            </div>

            <Card className="p-5 border-border/80 dashboard-hero">
                <div className="font-heading text-3xl uppercase tracking-wide" data-testid="vendor-detail-name">{data.vendor}</div>
                <div className="text-sm text-muted-foreground">
                    Active from {fmtDate(data.first_bill_date)} · last bill {fmtDate(data.last_bill_date)}
                </div>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Kpi label="Bills" value={data.bills_count} testid="vendor-kpi-bills" />
                    <Kpi label="Total spend" value={fmtMoney(data.total_spend)} testid="vendor-kpi-spend" />
                    <Kpi label="Avg per bill" value={fmtMoney(data.avg_spend)} testid="vendor-kpi-avg" />
                    <Kpi label="Months active" value={data.months.length} testid="vendor-kpi-months" />
                </div>
            </Card>

            <VendorDetailsCard data={data} />

            <VendorPaymentsPanel vendor={data} onChange={load} />

            <OpeningBalanceEditor
                entityLabel="Vendor"
                value={{
                    opening_balance_amount: data.opening_balance_amount,
                    opening_balance_direction: data.opening_balance_direction,
                    opening_balance_as_of: data.opening_balance_as_of,
                }}
                directionOptions={[
                    {
                        value: 'we_owe',
                        label: 'We owe them (Cr / Payable)',
                        shortLabel: 'We owe them (Cr)',
                        hint: 'Vendor is a creditor. Money is due to them.',
                    },
                    {
                        value: 'vendor_owes',
                        label: 'Vendor owes us (Dr / Advance)',
                        shortLabel: 'Vendor owes (Dr)',
                        hint: 'You paid them in advance or hold a credit note.',
                    },
                ]}
                onSave={async (patch) => {
                    await api.updateVendorMeta(data.vendor_key, patch);
                    await load();
                }}
                testid="vendor-opening-balance"
            />

            <VendorReconciliationPanel
                vendorKey={data?.vendor_key || data?.key}
                vendorName={data?.vendor || data?.name}
            />

            {chartData.length > 1 && (
                <Card className="p-5 border-border/80">
                    <div className="font-heading text-lg mb-3">Monthly spend</div>
                    <div style={{ width: '100%', height: 220 }} data-testid="vendor-monthly-chart">
                        <ResponsiveContainer>
                            <BarChart data={chartData} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
                                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                                <XAxis dataKey="month" fontSize={12} stroke="hsl(var(--muted-foreground))" />
                                <YAxis fontSize={12} stroke="hsl(var(--muted-foreground))" />
                                <RTooltip
                                    formatter={(v, k) => (k === 'total' ? fmtMoney(v) : v)}
                                    contentStyle={{ borderRadius: 8, borderColor: 'hsl(var(--border))' }}
                                    labelFormatter={(l) => fmtMonth(l)}
                                />
                                <Bar dataKey="total" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4" data-testid="vendor-monthly-sections">
                    {data.months.map((m) => (
                        <Card key={m.month} className="p-4 border-border/80">
                            <div className="flex items-baseline justify-between mb-3">
                                <div>
                                    <div className="font-heading text-lg">{fmtMonth(m.month)}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {m.count} bill{m.count > 1 ? 's' : ''}
                                    </div>
                                </div>
                                <div className="font-mono tabular-nums text-lg">{fmtMoney(m.total)}</div>
                            </div>
                            <div className="overflow-hidden rounded-md border border-border">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/40 text-muted-foreground">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-medium">Bill date</th>
                                            <th className="text-left px-3 py-2 font-medium">Invoice #</th>
                                            <th className="text-right px-3 py-2 font-medium">Items</th>
                                            <th className="text-right px-3 py-2 font-medium">Total</th>
                                            <th className="text-right px-3 py-2 font-medium">Open</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {m.bills.map((b) => (
                                            <tr key={b.id} className="border-t border-border hover:bg-muted/40">
                                                <td className="px-3 py-2">{fmtDate(b.bill_date)}</td>
                                                <td className="px-3 py-2 text-muted-foreground">{b.invoice_number || '—'}</td>
                                                <td className="px-3 py-2 text-right tabular-nums font-mono">{(b.items || []).length}</td>
                                                <td className="px-3 py-2 text-right tabular-nums font-mono">{fmtMoney(b.total, b.currency)}</td>
                                                <td className="px-3 py-2 text-right">
                                                    <Link
                                                        to={`/bills/${b.id}`}
                                                        className="text-primary hover:underline text-xs"
                                                        data-testid={`vendor-open-bill-${b.id}`}
                                                    >
                                                        open
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    ))}
                </div>

                <Card className="p-4 border-border/80 h-max" data-testid="vendor-top-items">
                    <div className="font-heading text-lg mb-3">Top items</div>
                    {(data.top_items || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">No items yet.</div>
                    ) : (
                        <ul className="space-y-2">
                            {data.top_items.map((it) => (
                                <li key={it.item_key} className="p-2 border border-border rounded-md">
                                    <Link
                                        to={`/items/detail?vendor_key=${encodeURIComponent(data.vendor_key)}&item_key=${encodeURIComponent(it.item_key)}`}
                                        className="font-medium text-sm hover:underline block truncate"
                                    >
                                        {it.item_name}
                                    </Link>
                                    <div className="flex items-center justify-between mt-1 text-xs">
                                        <span className="text-muted-foreground">{it.count} bills</span>
                                        <span className="font-mono tabular-nums">
                                            {fmtMoney(it.min_price)} – {fmtMoney(it.max_price)}
                                        </span>
                                    </div>
                                    {it.min_price !== it.max_price && (
                                        <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                                            Latest {fmtMoney(it.latest_price)}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>

            <UploadedLedgersSection
                vendorKey={data.vendor_key}
                vendorLabel={data.vendor}
            />

            <VendorRenameMergeDialog
                open={renameOpen}
                onOpenChange={setRenameOpen}
                currentVendor={{ vendor: data.vendor, vendor_key: data.vendor_key }}
                onSuccess={(res) => {
                    // If vendor_key changed, redirect to the new key; otherwise reload
                    if (res?.final_vendor_key && res.final_vendor_key !== data.vendor_key) {
                        nav(`/vendors/${encodeURIComponent(res.final_vendor_key)}`, { replace: true });
                    } else {
                        load();
                    }
                }}
            />
        </div>
    );
}
